import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Science as ScienceIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from "@mui/icons-material";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import LabReportView from "../Laboratory/LabReportView";

export default function LabReportModal({
  open,
  onClose,
  resultId,
  patientName,
  isDark: propIsDark,
}) {
  const { mode } = useThemeMode();
  const isDark = propIsDark !== undefined ? propIsDark : mode === "dark";
  const [fullScreen, setFullScreen] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : "12px",
          bgcolor: isDark ? "#121212" : "#FFFFFF",
          backgroundImage: "none",
          p: 0,
          overflow: "hidden",
          width: fullScreen
            ? "100vw !important"
            : { xs: "95vw", sm: "90vw", md: "860px", lg: "920px" },
          maxWidth: fullScreen ? "100vw !important" : "940px !important",
          height: fullScreen ? "100vh !important" : { xs: "88vh", sm: "82vh", md: "80vh" },
          maxHeight: fullScreen ? "100vh !important" : "84vh",
          margin: fullScreen ? "0 !important" : "auto !important",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          border: fullScreen
            ? "none"
            : `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "#E2E8F0"}`,
          boxShadow: isDark
            ? "0 25px 70px rgba(0,0,0,0.85)"
            : "0 25px 70px rgba(15,23,42,0.15)",
        },
      }}
    >
      {/* Top Dialog Bar */}
      <Box
        sx={{
          px: fullScreen ? 2.5 : 1.75,
          py: fullScreen ? 1 : 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0"}`,
          bgcolor: isDark ? "#1E1E1E" : "#F8FAFC",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              p: 0.7,
              borderRadius: "7px",
              bgcolor: "rgba(124, 58, 237, 0.12)",
              color: "#7C3AED",
              display: "flex",
            }}
          >
            <ScienceIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 800,
                fontSize: fullScreen ? "1.05rem" : "0.92rem",
                color: isDark ? "#F8FAFC" : "#0F172A",
                lineHeight: 1.2,
              }}
            >
              Laboratory Diagnostic Findings
            </Typography>
            {patientName && (
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: fullScreen ? "0.8rem" : "0.74rem",
                  color: isDark ? "#94A3B8" : "#64748B",
                  fontWeight: 500,
                }}
              >
                Patient: <strong>{patientName}</strong>
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label="Verified & Released"
            size="small"
            sx={{
              bgcolor: isDark ? "rgba(22, 163, 74, 0.15)" : "#DCFCE7",
              color: "#16A34A",
              fontWeight: 700,
              fontSize: "0.72rem",
              fontFamily: "'Satoshi', sans-serif",
              border: "1px solid rgba(22, 163, 74, 0.2)",
            }}
          />

          {/* Fullscreen / Maximize Toggle */}
          <Tooltip title={fullScreen ? "Restore normal size" : "Expand to Full Screen"}>
            <IconButton
              onClick={() => setFullScreen((prev) => !prev)}
              size="small"
              sx={{
                color: isDark ? "#94A3B8" : "#64748B",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
                borderRadius: "8px",
                "&:hover": {
                  color: isDark ? "#F8FAFC" : "#0F172A",
                  bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9",
                },
              }}
            >
              {fullScreen ? (
                <FullscreenExitIcon sx={{ fontSize: 18 }} />
              ) : (
                <FullscreenIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>

          {/* Close Dialog */}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: isDark ? "#94A3B8" : "#64748B",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#E2E8F0"}`,
              borderRadius: "8px",
              "&:hover": {
                color: isDark ? "#F8FAFC" : "#0F172A",
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#F1F5F9",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Report Body */}
      <DialogContent
        sx={{
          p: fullScreen ? { xs: 1.5, sm: 2 } : { xs: 0.75, sm: 1 },
          bgcolor: isDark ? "#121212" : "#F8FAFC",
          overflowY: "auto",
          flexGrow: 1,
        }}
      >
        {resultId ? (
          <LabReportView
            isDark={isDark}
            resultId={resultId}
            onBack={onClose}
            isModal={true}
            fullScreen={fullScreen}
            readOnly={true}
          />
        ) : (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Typography sx={{ color: isDark ? "#94A3B8" : "#64748B", fontFamily: "'Satoshi', sans-serif" }}>
              No laboratory report selected.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
